import Menu, { MenuItem } from 'rc-menu';
import { Component, ReactElement } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DownloadButton } from '../../../../../../shared/frontend/components/downloadButton';
import { Media } from '../../../../../../shared/frontend/components/presentational/media';
import history, { handleRCMenuOnClick, navigateTo } from '../../../../../../shared/frontend/libs/history';
import { DIFFICULTIES } from '../../../../shared/enums';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import withQueueModal from '../../../HOCS/withQueueModal';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { TSiteMetaClientState } from '../../libs/store';
import { specsSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import { buildRouteParams } from './menuHelper';
import { buildMenus, menuBuiltinPlacements } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { getMenuItems } from './menuItems';
import { globalSearchActions } from '../../../../../../shared/frontend/scripts/global-search/store/modules';
import { Col } from '../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../shared/frontend/components/grid/Row';

type TPropsFromState = TSiteMetaClientState & {
  zone?: string;
  combinedRegions?: any[];
  isDungeon?: boolean;
  isRaid?: boolean;
  isCharacter?: boolean;
  isTournament?: boolean;
  showQueueModal: () => void;
};

export type TDesktopSiteNavProps = TPropsFromState & RouteComponentProps<any, any>;

function onClickMenu(props: any, params: any) {
  const { key } = params;
  switch (key) {
    case 'update_now':
      props.showQueueModal();
      break;
    case 'new-season-recap':
      // Custom key for duplicate /tww-review menu item
      navigateTo('/tww-review', params.domEvent);
      break;
    default:
      handleRCMenuOnClick(params);
      break;
  }
}

/**
 * DesktopSiteNavContentLarge
 */
const DesktopSiteNavContentLarge = withTranslation()(
  withQueueModal((props: TDesktopSiteNavProps) => {
    const { t } = props;
    return (
      <div className="rio-hidden--small rio-hidden--medium rio-desktop-nav">
        <div className="rio-tabs--scoped slds-tabs--scoped">
          <div className="rio-title-container">
            <Row id="rio-desktop-nav-menu-container">
              <Col>
                <Menu
                  selectable={false}
                  mode="horizontal" disabledOverflow={true}
                  className="rio-title-menu rio-desktop-nav-menu"
                  onClick={onClickMenu.bind(null, props)}
                  builtinPlacements={menuBuiltinPlacements}
                >
                  {buildMenus('menus', getMenuItems(props))}
                  {!props.currentFeatures.premium && (
                    <MenuItem key="/premium">
                      <div className="text-bold text-premium-special text-premium-special--animated">
                        {t('nav:main.go-premium')}
                      </div>
                    </MenuItem>
                  )}
                </Menu>
              </Col>
              <Col className="slds-text-align--left">
                <DownloadButton
                  onClick={(ev: any) => {
                    RIO_TRACK_EVENT('event', 'select_content', {
                      content_type: 'view_addon_page'
                    });
                    navigateTo('/addon', ev);
                  }}
                  style={{
                    position: 'relative',
                    top: 4
                  }}
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  })
);

class DesktopSiteNavContentSmallBase extends Component<TDesktopSiteNavProps, {}> {
  public updateFilter(name: string, event: any) {
    const newRouteParams = buildRouteParams(this.props.match.params, { [name]: event.target.value });
    const path = resolveRoutePath(this.props.match.path, newRouteParams);
    history.push(path);
  }

  public render(): ReactElement<TDesktopSiteNavProps> {
    if (!this.props.match.params.raid || !this.props.match.params.raid.length) {
      return <div />;
    }

    return (
      <div className="slds-context-bar">
        <nav
          className="slds-context-bar__primary slds-form--inline rio-context-bar slds-align--absolute-center"
          role="navigation"
        >
          <label className="slds-form-element__label text-muted" htmlFor="filter-region">
            Region:
          </label>
          &nbsp;
          <div className="slds-form-element">
            <div className="slds-form-element__control">
              <div className="slds-select_container">
                <select
                  id="filter-region"
                  className="slds-select"
                  onChange={this.updateFilter.bind(this, 'region')}
                  defaultValue={this.props.match.params.region}
                >
                  {this.props.regions
                    .filter((region: any) => region.slug !== 'cn' || this.props.isTournament)
                    .map((region: any, index: number) => (
                      <option key={index} value={region.slug}>
                        {region.short_name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
          <label className="slds-form-element__label text-muted rio-filter-margin" htmlFor="filter-difficulty">
            Difficulty:
          </label>
          &nbsp;
          <div className="slds-form-element">
            <div className="slds-form-element__control">
              <div className="slds-select_container">
                <select
                  id="filter-difficulty"
                  className="slds-select"
                  onChange={this.updateFilter.bind(this, 'difficulty')}
                  defaultValue={this.props.match.params.difficulty}
                >
                  {DIFFICULTIES.map((difficulty: any, index: number) => (
                    <option key={index} value={difficulty.slug}>
                      {difficulty.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </nav>
      </div>
    );
  }
}

const DesktopSiteNavContentSmall = withTranslation()(DesktopSiteNavContentSmallBase);

function DesktopSiteNavBase(props: TDesktopSiteNavProps) {
  const computedProps = {
    combinedRegions: [...props.regions, ...(props.subRegions || [])],
    zone: props.match.params.raid || props.match.params.dungeon,
    isDungeon: !!props.match.params.dungeon,
    isRaid: !!props.match.params.raid,
    isCharacter: !!props.match.params.class,
    isTournament:
      [
        namedRoutes.KeystoneTeamRankingsMdi2017,
        namedRoutes.KeystoneTeamDetailsMdi2017,
        namedRoutes.MythicDungeonInvitational2017Landing,
        namedRoutes.MythicDungeonInvitational2017Overview,
        namedRoutes.MythicDungeonInvitational2018ProvingGrounds,
        namedRoutes.MythicDungeonInvitational2018Overview,
        namedRoutes.KeystoneTeamRankingsMdi2018,
        namedRoutes.KeystoneTeamDetailsMdi2018,
        namedRoutes.KeystoneTimeTrialsTeamDetailsMdi2018,
        namedRoutes.MythicDungeonInvitationalSpring2019ProvingGrounds,
        namedRoutes.MythicDungeonInvitationalSpring2019Overview,
        namedRoutes.KeystoneTeamRankingsMdiSpring2019,
        namedRoutes.KeystoneTeamDetailsMdiSpring2019,
        namedRoutes.KeystoneTimeTrialsTeamDetailsMdiSpring2019,
        namedRoutes.MythicDungeonInvitationalSummer2019Overview,
        namedRoutes.KeystoneTeamRankingsMdiSummer2019,
        namedRoutes.KeystoneTeamDetailsMdiSummer2019,
        namedRoutes.KeystoneTimeTrialsTeamDetailsMdiSummer2019
      ].indexOf(props.match.path) !== -1
  };

  return (
    <>
      <Media lessThan="lg">
        <DesktopSiteNavContentSmall {...props} {...computedProps} />
      </Media>
      <Media greaterThanOrEqual="lg">
        <DesktopSiteNavContentLarge {...props} {...computedProps} />
      </Media>
    </>
  );
}

// take state from store and convert into props
function mapStateToProps(state: any, ownProps: TDesktopSiteNavProps): TPropsFromState {
  return {
    currentFeatures: state.currentFeatures,
    ...ownProps,
    ...state.siteMeta, // deprecated spread
    ...state.siteConfig,
    siteMeta: state.siteMeta,
    siteConfig: state.siteConfig,
    specs: specsSelector(state)
  };
}

const mapDispatchToProps = {
  resetPagination: globalSearchActions.resetPagination
};

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect<TPropsFromState, TDesktopSiteNavProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(DesktopSiteNavBase);
